// Short badge labels for dorm items in the 2D floor-plan view.
//
// Keyed by item typeKey. Kept in sync with the optimizer's emit list
// (`ITEM_DIMS_INCHES` in shared-lib/dorm-optimizer-v2/constants.js) — the
// dorm-badge-coverage test asserts every emittable key resolves to a badge, so
// new items must be added here (or covered by the dorm-bed*/dorm-bunk* prefix
// rule in getDormBadge below).
import { KNICKKNACK_DIMS } from '@shared-lib/dorm-styling/plan/constants';

export const DORM_BADGE_MAP = {
  'dorm-desk-chair': 'Desk',
  'dorm-desk-hutch-chair': 'Desk',
  'dorm-desk': 'Desk',
  'dorm-desk-hutch': 'Desk',
  'dorm-chair': 'Chair',
  'dorm-dresser': 'Drsr',
  'dorm-wardrobe': 'Wrdb',
  'dorm-bookshelf': 'Bksh',
  'dorm-bookshelf-wide': 'Bksh',
  'dorm-stackable-drawers': 'Drw',
  'dorm-rolling-cart': 'Cart',
  'dorm-shoe-rack': 'Shoe',
  'dorm-storage-bin': 'Stor',
  'dorm-laundry-hamper': 'Hmpr',
  'dorm-minifridge-compact': 'FR',
  'dorm-minifridge-midsize': 'FR',
  // Current canonical optimizer keys (post July-14 dorm item normalization).
  // The variant keys above are retained for pre-normalization persisted objects.
  'dorm-mini-fridge': 'FR',
  'dorm-laundry-bin': 'Hmpr',
  'dorm-3-tier-cart': 'Cart',
  'dorm-microwave': 'MW',
  'dorm-printer': 'Prnt',
  'dorm-tower-fan': 'Fan',
  'dorm-desk-fan': 'Fan',
  'dorm-fan': 'Fan',
  'dorm-air-purifier-tower': 'Air',
  'dorm-air-purifier-compact': 'Air',
  'dorm-air-purifier': 'Air',
  'dorm-stick-vacuum': 'Vac',
  'dorm-handheld-vacuum': 'Vac',
  'dorm-beanbag': 'Bean',
  'dorm-rug': 'Rug',
  'dorm-rug-circular': 'Rug',
  'dorm-desk-plant': 'Plt',
  'dorm-floor-plant': 'Plt',
  'dorm-fairy-lights': 'Lts',
  'dorm-fairy-lights-curtain': 'Lts',
  'dorm-surge-protector': 'Pwr',
  'dorm-command-hook': 'Hook',
  'dorm-wastebasket': 'Bin',
  'dorm-corkboard': 'Cork',
  'dorm-whiteboard': 'WB',
  'dorm-poster': 'Pstr',
  'dorm-tapestry': 'Tap',
  // Normalized persisted dorm keys (dorm item normalization).
  'dorm-monitor': 'Mon',
  'dorm-tv': 'TV',
  'dorm-desk-lamp': 'Lamp',
  'dorm-floor-lamp': 'Lamp',
  'dorm-mirror': 'Mir',
  'dorm-command-hooks': 'Hook',
  'dorm-floating-shelf': 'Shlf',
  'dorm-over-door-hooks': 'Hook',
  'dorm-over-door-mirror': 'Mir',
  'dorm-trash-can': 'Bin',
  // Style-It surface knickknacks.
  'dorm-rubiks-cube': 'Cube',
  'dorm-plush': 'Plsh',
  'dorm-book-stack': 'Bks',
  'dorm-mug': 'Mug',
  'dorm-snack-stack': 'Snck',
  'dorm-candle': 'Cndl',
  // Theme knickknacks. Without a badge these draw as an unlabelled filled
  // rectangle with a drawer-pull line stacked on the desk — the "mystery
  // cabinet" this map exists to prevent.
  'dorm-shell-cluster': 'Shel',
  'dorm-starfish': 'Star',
  'dorm-boombox': 'Boom',
  'dorm-cassette-stack': 'Csst',
  'dorm-camp-lantern': 'Lntn',
  'dorm-fish-bowl': 'Fish',
  'dorm-terrarium': 'Terr',
  'dorm-dried-bundle': 'Stem',
  'dorm-plush-dog': 'Dog',
  'dorm-plush-cat': 'Cat',
  // Theme wall art. `Rttn` rather than `Fan` — `Fan` is already the box fan.
  'dorm-wall-oar': 'Oar',
  'dorm-rattan-fan': 'Rttn',
  'dorm-vinyl-wall': 'Vnyl',
  'dorm-neon-sign': 'Neon',
  'dorm-pennant-banner': 'Pnnt',
  // Floor decor.
  'dorm-pet-bed': 'PetB',
};

/**
 * Small surface decor — items that sit ON furniture tops in plan view. The 2D
 * floorplan draws these as a light open circle rather than the generic dorm
 * rectangle-with-handle: opaque boxes stacked on the desk rectangle read as
 * mystery cabinets.
 */
export const DORM_SMALL_DECOR_TYPES = new Set([
  // Everything the surface planner can seat on a desk or dresser top IS small
  // decor — so derive it rather than keep a parallel list that silently falls
  // behind. (Verified: this reproduces the previous hand-written set exactly.)
  // `dorm-desk-plant` is the one surface item outside the knickknack registry.
  'dorm-desk-plant',
  ...Object.keys(KNICKKNACK_DIMS),
]);

export const isDormSmallDecor = (typeKey) =>
  DORM_SMALL_DECOR_TYPES.has(typeKey);

// Resolve a dorm item's short floor-plan badge. Beds/bunks share one badge via
// a prefix rule (they carry height-qualified keys). Returns null when unmapped.
export const getDormBadge = (typeKey) => {
  if (DORM_BADGE_MAP[typeKey]) return DORM_BADGE_MAP[typeKey];
  // Beds/bunks: the base dimension key plus any height-qualified variant
  // (e.g. dorm-bed-loft-xl) all share the "Bed" badge.
  if (
    typeKey === 'dorm-bed' ||
    typeKey === 'dorm-bunk' ||
    typeKey.startsWith('dorm-bed-') ||
    typeKey.startsWith('dorm-bunk-')
  )
    return 'Bed';
  return null;
};
